/* eslint-disable no-unused-vars */
import { yupResolver } from "@hookform/resolvers/yup";
import { YupCore } from "core/yup";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { ObjectSchema } from "yup";

interface FormProps {
  initialValues?: any;
  children: ReactNode;
  initialFormDisabled?: boolean;
  initialSchemaValidation?: ObjectSchema<any>;
  onSubmit?: (dataForm: any) => void;
}

interface FormContextProps {
  methodsForm: UseFormReturn;
  formDisabled: boolean;
  setFormDisabled: (value: boolean) => void;
  setSchemaValidation: (schema: ObjectSchema<any>) => void;
  setValuesForm: (data: any) => void;
  data: any;
  setData: (value: any) => void;
  validateFields: (fieldsName: string[]) => Promise<boolean>;
}

const FormContext = createContext({} as FormContextProps);

const Form = ({
  children,
  onSubmit,
  initialFormDisabled,
  initialValues,
  initialSchemaValidation,
}: FormProps) => {
  const [formSchema, setFormSchema] = useState<ObjectSchema<any>>(
    () => initialSchemaValidation || YupCore.object().shape({})
  );

  const [formDisabled, setFormDisabled] = useState(false);

  const [data, setData] = useState({} as any);

  const setSchemaValidation = useCallback((schema: ObjectSchema<any>) => {
    setFormSchema((prevent) => prevent.concat(schema));
  }, []);

  const methodsForm = useForm({
    resolver: yupResolver(formSchema),
    mode: "onChange",
  });

  const {
    reset,
    getValues,
    formState: { errors },
  } = methodsForm;

  const setValuesForm = useCallback(
    (data: any) => {
      reset({ ...getValues(), ...data });
    },
    [getValues, reset]
  );

  async function validateFields(fieldsName: string[]): Promise<boolean> {
    await methodsForm.trigger(fieldsName);

    const isFieldsWithErrors = fieldsName.find((field) => !!errors[field]);

    return isFieldsWithErrors ? false : true;
  }

  useEffect(() => {
    setFormDisabled(initialFormDisabled ? true : false);
  }, [initialFormDisabled]);

  useEffect(() => {
    reset({ ...initialValues });
  }, [initialValues, reset]);

  return (
    <FormContext.Provider
      value={{
        methodsForm,
        formDisabled,
        setFormDisabled,
        setSchemaValidation,
        setValuesForm,
        data,
        setData,
        validateFields,
      }}
    >
      <form onSubmit={onSubmit && methodsForm.handleSubmit(onSubmit)}>
        {children}
      </form>
    </FormContext.Provider>
  );
};

function useFormContext() {
  const context = useContext(FormContext);

  return context;
}

export { Form, useFormContext };
